import { useCreateOrUpdateMultipleprintInBibliography } from "@/api/related-fields";
import { LinkableType } from "@/components/LinksManagement";
import {
  applyFieldConfigOverrides,
  FormLayoutElement,
} from "@/config/forms/general";
import { MultipleprintInBibliography } from "@/lib/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import LinksDrawerLayout from "./LinksDrawerLayout";

const MultipleprintInBibliographyDrawer = ({
  fields,
  data,
  queryId,
  type,
  open,
  onOpenChange,
}: {
  fields: FormLayoutElement[] | undefined;
  data: MultipleprintInBibliography;
  queryId: string;
  type: LinkableType;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) => {
  const schema = z.object({
    bibliographieDansEstampesMultiplesDescriptionReproduitPage: z
      .string()
      .optional(),
    bibliographieDansEstampesMultiplesDescriptionReproduitPlanche: z
      .string()
      .optional(),
    biblioInMultiPrintDescPlancheNonNum: z
      .boolean()
      .optional(),
    bibliographieDansEstampesMultiplesDescriptionCitePage: z
      .string()
      .optional(),
    bibliographieDansEstampesMultiplesDescriptionListePage: z
      .string()
      .optional(),
    bibliographieDansEstampesMultiplesDescriptionSousNumero: z
      .string()
      .optional(),
    bibliographieDansEstampesMultiplesDescriptionPhotographe: z
      .string()
      .optional(),
    bibliographieDansEstampesMultiplesDescriptionReproductionCoul: z
      .boolean()
      .optional(),
    biblioInMultiPrintDescReproCover: z
      .boolean()
      .optional(),
    bibliographieDansEstampesMultiplesDescriptionNonPagine: z
      .boolean()
      .optional(),
    bibliographieDansEstampesMultiplesDescriptionTexteLibre: z
      .string()
      .optional(),
    bibliographieDansEstampesMultiplesDescriptionExemplaireRepr: z
      .string()
      .optional(),
    bibliography: z.object({
      bibliographieDescriptionNumeroBibliographie: z.string().optional(),
      bibliographieDescriptionResumeLong: z.string().optional(),
    }),
  });

  const { mutate: createOrUpdateMultipleprintInBibliography } =
    useCreateOrUpdateMultipleprintInBibliography(queryId, type);

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      bibliography: data.bibliography,
      bibliographieDansEstampesMultiplesDescriptionReproduitPage:
        data.bibliographieDansEstampesMultiplesDescriptionReproduitPage,
      bibliographieDansEstampesMultiplesDescriptionReproduitPlanche:
        data.bibliographieDansEstampesMultiplesDescriptionReproduitPlanche,
      biblioInMultiPrintDescPlancheNonNum:
        data.biblioInMultiPrintDescPlancheNonNum,
      bibliographieDansEstampesMultiplesDescriptionCitePage:
        data.bibliographieDansEstampesMultiplesDescriptionCitePage,
      bibliographieDansEstampesMultiplesDescriptionListePage:
        data.bibliographieDansEstampesMultiplesDescriptionListePage,
      bibliographieDansEstampesMultiplesDescriptionSousNumero:
        data.bibliographieDansEstampesMultiplesDescriptionSousNumero,
      bibliographieDansEstampesMultiplesDescriptionPhotographe:
        data.bibliographieDansEstampesMultiplesDescriptionPhotographe,
      bibliographieDansEstampesMultiplesDescriptionReproductionCoul:
        data.bibliographieDansEstampesMultiplesDescriptionReproductionCoul,
      biblioInMultiPrintDescReproCover: data.biblioInMultiPrintDescReproCover,
      bibliographieDansEstampesMultiplesDescriptionNonPagine:
        data.bibliographieDansEstampesMultiplesDescriptionNonPagine,
      bibliographieDansEstampesMultiplesDescriptionTexteLibre:
        data.bibliographieDansEstampesMultiplesDescriptionTexteLibre,
      bibliographieDansEstampesMultiplesDescriptionExemplaireRepr:
        data.bibliographieDansEstampesMultiplesDescriptionExemplaireRepr,
    },
  });

  const onSubmit = (formData: z.infer<typeof schema>) => {
    createOrUpdateMultipleprintInBibliography({
      id: data.id,
      multipleprintId: data.multipleprint.id,
      bibliographyId: data.bibliography.id,
      ...formData,
    });
  };
  const isFromBibliography = type === "bibliography";
  const previewConfig = isFromBibliography
    ? {
        attachments: data.multipleprint?.attachments ?? [],
        type: "multipleprint" as const,
        alt:
          data.multipleprint?.estampesMultiplesDescriptionNumeroEnregistrement ??
          data.multipleprint?.estampesMultiplesDescriptionTitre,
      }
    : {
        attachments: data.bibliography?.attachments ?? [],
        type: "bibliography" as const,
        alt:
          data.bibliography?.bibliographieDescriptionNumeroBibliographie ??
          data.bibliography?.bibliographieDescriptionTitre,
      };
  const enhancedFields = applyFieldConfigOverrides(fields ?? [], {
    bibliographieDansEstampesMultiplesDescriptionTexteLibre: {
      expandOnOverflow: true,
    },
  });

  return (
    <LinksDrawerLayout
      form={form}
      fields={enhancedFields}
      recordId={data.id}
      onSubmit={onSubmit}
      previewImage={previewConfig}
      open={open}
      onOpenChange={onOpenChange}
    />
  );
};

export default MultipleprintInBibliographyDrawer;
